import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/dashboard/StatCard";
import { CompanyProfileCard } from "@/components/dashboard/CompanyProfileCard";
import { DraftsCard } from "@/components/dashboard/DraftsCard";
import { RosterCard } from "@/components/dashboard/RosterCard";
import { RecentBookingsCard, QuickActionsRail } from "@/components/dashboard/RecentBookingsCard";
import { Wallet, Inbox, FileKey, BadgeDollarSign, Plus } from "lucide-react";

export default function Dashboard() {
  const { user, loading: authLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCompany, setEditingCompany] = useState(false);
  const [drafts, setDrafts] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setStats(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    if (user?.role === "client") {
      try {
        const { data } = await api.get("/drafts");
        setDrafts(data);
      } catch (e) {
        // Drafts are non-critical; degrade silently so the dashboard still renders.
        setDrafts([]);
      }
    }
  }, [user]);

  const deleteDraft = async (draftId) => {
    try {
      await api.delete(`/drafts/${draftId}`);
      toast.success("Draft deleted");
      loadDrafts();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete draft"));
    }
  };

  useEffect(() => {
    if (user) {
      load();
      loadDrafts();
    }
  }, [user, load, loadDrafts]);

  if (authLoading || loading) {
    return (
      <div className="page-container space-y-6 py-10">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  const isClient = user.role === "client";
  const isAgency = user.role === "agency";

  return (
    <div className="page-container py-8 sm:py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{user.role} dashboard</p>
          <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Hello, {user.name?.split(" ")[0]}</h1>
        </div>
        {!isClient && (
          (isAgency || (stats?.models_count === 0)) && (
            <Button data-testid="dashboard-add-model-button" className="rounded-xl" onClick={() => navigate("/models/new")}>
              <Plus className="mr-1.5 h-4 w-4" /> {isAgency ? "Add model to roster" : "Create my model profile"}
            </Button>
          )
        )}
      </div>

      {/* STATS */}
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Wallet} label="Wallet" value={fmtMoney(stats?.wallet_balance)} testId="dashboard-wallet-stat" />
        {isClient ? (
          <>
            <StatCard icon={Inbox} label="Open negotiations" value={stats?.pending_requests ?? 0} testId="dashboard-pending-stat" />
            <StatCard icon={FileKey} label="Active licences" value={stats?.active_licenses ?? 0} testId="dashboard-licenses-stat" />
            <StatCard icon={BadgeDollarSign} label="Total invested" value={fmtMoney(stats?.total_spent)} testId="dashboard-spent-stat" />
          </>
        ) : (
          <>
            <StatCard icon={BadgeDollarSign} label="Total earnings" value={fmtMoney(stats?.total_earnings)} testId="dashboard-earnings-stat" />
            <StatCard icon={Inbox} label="Pending requests" value={stats?.pending_requests ?? 0} testId="dashboard-pending-stat" />
            <StatCard icon={FileKey} label="Active licences" value={stats?.active_licenses ?? 0} testId="dashboard-licenses-stat" />
          </>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          {isClient && (
            <CompanyProfileCard
              stats={stats}
              editing={editingCompany}
              onEdit={() => setEditingCompany(true)}
              onSaved={() => {
                setEditingCompany(false);
                load();
                refresh();
              }}
            />
          )}
          {isClient && <DraftsCard drafts={drafts} onDelete={deleteDraft} />}
          {!isClient && <RosterCard user={user} stats={stats} isAgency={isAgency} />}
          <RecentBookingsCard stats={stats} isClient={isClient} />
        </div>
        <QuickActionsRail isClient={isClient} />
      </div>
    </div>
  );
}
